import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  Object3D,
  ShapeGeometry,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import {
  pointInPolygon,
  type PolygonWithHoles,
} from "../../world/geometry.ts";
import {
  isAntakalnisSuburbanBackgroundCandidate,
  isNaujamiestisIndustrialBackgroundCandidate,
  isSnipiskesWoodenBackgroundCandidate,
  isSovietPanelBackgroundCandidate,
} from
  "../../world/buildings/vilniusStyleZones.ts";
import type {
  RuntimeAreaFeature,
  RuntimeLinearFeature,
  RuntimeTileFeatureCounts,
  RuntimeTileLodLevel,
  RuntimeTileLodPayload,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type {
  BuildingFootprintData,
  TerrainTileData,
  WorldTileId,
} from "../../world/schema/tile.ts";
import { sampleTerrainTileData } from "../../world/terrain.ts";
import {
  streetFeatureWidthM,
  streetSurfaceFamilyForTile,
  terrainWithAuthoredStreetEarthworks,
} from "../../world/streets.ts";
import {
  runtimeWaterSurfaceElevationM,
  runtimeWaterBankWidthsM,
  terrainWithRuntimeWaterCarving,
} from "../../world/water.ts";
import {
  createDiagnosticFootprintGeometry,
  createPolygonShape,
  createTerrainGeometry,
} from "./developmentTileSetView.ts";
import type { BuildingRenderConfig } from "../../config/buildings.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import { createStylizedBuildingBatch } from "./stylizedBuildings.ts";
import { createStreetLightBatch } from "./streetLights.ts";
import { createBusStopBatch } from "./busStops.ts";
import { createTrafficSignalBatch } from "./trafficSignals.ts";
import { createRoadMarkingBatch } from "./roadMarkings.ts";
import { createRoadSignBatch } from "./roadSigns.ts";
import { createCrossingDetailBatch } from "./crossingDetails.ts";
import { createStreetFurnitureBatch } from "./streetFurniture.ts";
import { createStreetNamePlaqueBatch } from "./streetNamePlaques.ts";
import {
  createReducedStreetSurfaceBatch,
  createStreetSurfaceBatch,
} from "./streetSurfaces.ts";
import {
  isAuthoredLandmarkFootprint,
  VILNIUS_AUTHORED_INTERIOR_SURFACE_MASKS,
} from "../../config/landmarks.ts";
import {
  ZVERYNAS_TIMBER_BACKGROUND_MAXIMUM_HEIGHT_M,
  ZVERYNAS_TIMBER_BACKGROUND_MAXIMUM_SPAN_M,
  ZVERYNAS_TIMBER_STYLE_TILE_IDS,
} from "../../config/zverynas.ts";
import { createRuntimeTreeBatch } from "./trees.ts";
import { vegetationInstancesForTile } from "../../world/municipalTrees.ts";
import { createUrbanStructureBatch } from "./urbanStructures.ts";
import { createRailwaySurfaceBatch } from "./railways.ts";

export interface RuntimeTileViewOptions {
  readonly lod?: RuntimeTileLodLevel;
  readonly showTileDiagnostics?: boolean;
  readonly showCollisionDebug?: boolean;
}

export interface RuntimeTileRenderedFeatureCounts {
  readonly buildingPlans: number;
  readonly transportLines: number;
  readonly waterLines: number;
  readonly landCoverPolygons: number;
  readonly trees: number;
  readonly busStops: number;
  readonly trafficSignals: number;
  readonly roadMarkingSegments: number;
  readonly roadSigns: number;
  readonly collisionFootprints: number;
}

export interface RuntimeTileRenderMetrics {
  readonly tileId: WorldTileId;
  readonly lod: RuntimeTileLodLevel;
  readonly geometryCount: number;
  readonly drawCallEstimate: number;
  readonly triangleCount: number;
  readonly drawCallsByGroup: Readonly<Record<string, number>>;
  readonly trianglesByGroup: Readonly<Record<string, number>>;
  readonly buildCpuMsByGroup: Readonly<Record<string, number>>;
  readonly rendered: RuntimeTileRenderedFeatureCounts;
  readonly source: RuntimeTileFeatureCounts;
  readonly collisionDebugVisible: boolean;
  readonly resourceVersion: number;
}

export interface RuntimeWorldTileView {
  readonly object: Group;
  readonly metrics: RuntimeTileRenderMetrics;
  getCameraObstructions(): readonly RuntimeTileCameraObstruction[];
  update(options: Readonly<RuntimeTileViewOptions>): RuntimeTileRenderMetrics;
  dispose(): void;
}

export interface RuntimeTileCameraObstruction {
  readonly id: string;
  readonly object: Object3D;
}

export interface StreamingWorldRenderDiagnostics {
  readonly attachedTileCount: number;
  readonly tilesByLod: Readonly<Record<RuntimeTileLodLevel, number>>;
  readonly geometryCount: number;
  readonly drawCallEstimate: number;
  readonly triangleCount: number;
  readonly drawCallsByGroup: Readonly<Record<string, number>>;
  readonly trianglesByGroup: Readonly<Record<string, number>>;
  readonly buildCpuMsByGroup: Readonly<Record<string, number>>;
  readonly buildCpuMsByTile: Readonly<
    Record<string, Readonly<Record<string, number>>>
  >;
  readonly collisionDebugTileCount: number;
  readonly attachOperations: number;
  readonly updateOperations: number;
  readonly detachOperations: number;
  readonly disposedGeometryCount: number;
}

export interface StreamingWorldView {
  readonly object: Group;
  attach(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
    options?: Readonly<RuntimeTileViewOptions>,
  ): RuntimeTileRenderMetrics;
  attachInPhases(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
    options: Readonly<RuntimeTileViewOptions>,
    yieldToNextFrame: () => Promise<void>,
    shouldContinue: () => boolean,
  ): Promise<RuntimeTileRenderMetrics | null>;
  update(
    tileId: WorldTileId,
    options: Readonly<RuntimeTileViewOptions>,
  ): RuntimeTileRenderMetrics;
  detach(tileId: WorldTileId): boolean;
  has(tileId: WorldTileId): boolean;
  getCameraObstructions(
    tileId: WorldTileId,
  ): readonly RuntimeTileCameraObstruction[];
  getDiagnostics(): StreamingWorldRenderDiagnostics;
  clear(): void;
  dispose(): void;
}

const COLLISION_MATERIAL = new LineBasicMaterial({
  color: 0xe08e67,
  transparent: true,
  opacity: 0.9,
  depthTest: false,
});
const TILE_DIAGNOSTIC_MATERIAL = new LineBasicMaterial({
  color: 0xc0a170,
  transparent: true,
  opacity: 0.88,
  depthTest: false,
});

const WATER_SURFACE_LIFT_M = 0.06;
const COLLISION_LINE_LIFT_M = 0.14;
const TILE_DIAGNOSTIC_LIFT_M = 0.18;
const LAND_COVER_SURFACE_LIFT_M = 0.002;
const INTERIOR_SURFACE_MASK_EDGE_M = 0.18;
const INTERIOR_PATH_MASK_SAMPLE_STEP_M = 0.4;

interface RuntimeInteriorSurfaceMask {
  readonly polygon: PolygonWithHoles;
  readonly bounds: Readonly<{
    minX: number;
    minZ: number;
    maxX: number;
    maxZ: number;
  }>;
  readonly transportFeatureIds: ReadonlySet<string>;
}

function interiorSurfaceMasksForTile(
  tileId: WorldTileId,
  tileOrigin: Readonly<{ x: number; z: number }>,
): readonly RuntimeInteriorSurfaceMask[] {
  return VILNIUS_AUTHORED_INTERIOR_SURFACE_MASKS
    .filter((mask) => mask.tileId === tileId)
    .map((mask) => {
      const outer = mask.outerWorldM.map((point) => ({
        x: point.x - tileOrigin.x,
        z: point.z - tileOrigin.z,
      }));
      return {
        polygon: { outer, holes: [] },
        bounds: {
          minX: Math.min(...outer.map(({ x }) => x)),
          minZ: Math.min(...outer.map(({ z }) => z)),
          maxX: Math.max(...outer.map(({ x }) => x)),
          maxZ: Math.max(...outer.map(({ z }) => z)),
        },
        transportFeatureIds: mask.transportFeatureIds,
      };
    });
}

function distanceToRingM(
  point: Readonly<AreaPoint>,
  ring: Readonly<PolygonWithHoles["outer"]>,
): number {
  let minimumM = Number.POSITIVE_INFINITY;
  for (let index = 0; index < ring.length; index += 1) {
    const start = ring[index];
    const end = ring[(index + 1) % ring.length];
    if (start === undefined || end === undefined) {
      continue;
    }
    const deltaX = end.x - start.x;
    const deltaZ = end.z - start.z;
    const lengthSquaredM = deltaX * deltaX + deltaZ * deltaZ;
    const t = lengthSquaredM <= 1e-8
      ? 0
      : Math.max(
        0,
        Math.min(
          1,
          ((point.x - start.x) * deltaX +
            (point.z - start.z) * deltaZ) / lengthSquaredM,
        ),
      );
    minimumM = Math.min(
      minimumM,
      Math.hypot(
        point.x - (start.x + deltaX * t),
        point.z - (start.z + deltaZ * t),
      ),
    );
  }
  return minimumM;
}

function interiorSurfaceMaskContains(
  mask: Readonly<RuntimeInteriorSurfaceMask>,
  point: Readonly<AreaPoint>,
  edgeM = INTERIOR_SURFACE_MASK_EDGE_M,
): boolean {
  if (
    point.x < mask.bounds.minX - edgeM ||
    point.x > mask.bounds.maxX + edgeM ||
    point.z < mask.bounds.minZ - edgeM ||
    point.z > mask.bounds.maxZ + edgeM
  ) {
    return false;
  }
  return pointInPolygon(point, mask.polygon) ||
    distanceToRingM(point, mask.polygon.outer) <= edgeM;
}

function transportFeaturesOutsideInteriorSurfaceMasks(
  features: readonly Readonly<RuntimeLinearFeature>[],
  masks: readonly RuntimeInteriorSurfaceMask[],
): readonly Readonly<RuntimeLinearFeature>[] {
  if (masks.length === 0) {
    return features;
  }
  return features.flatMap((feature) => {
    const mask = masks.find(({ transportFeatureIds }) =>
      transportFeatureIds.has(feature.id)
    );
    if (mask === undefined || feature.points.length < 2) {
      return [feature];
    }
    const sampled: AreaPoint[] = [];
    for (let index = 1; index < feature.points.length; index += 1) {
      const start = feature.points[index - 1];
      const end = feature.points[index];
      if (start === undefined || end === undefined) {
        continue;
      }
      const segmentCount = Math.max(
        1,
        Math.ceil(
          Math.hypot(end.x - start.x, end.z - start.z) /
            INTERIOR_PATH_MASK_SAMPLE_STEP_M,
        ),
      );
      for (
        let segmentIndex = index === 1 ? 0 : 1;
        segmentIndex <= segmentCount;
        segmentIndex += 1
      ) {
        const t = segmentIndex / segmentCount;
        sampled.push({
          x: start.x + (end.x - start.x) * t,
          z: start.z + (end.z - start.z) * t,
        });
      }
    }
    const runs: AreaPoint[][] = [];
    let current: AreaPoint[] = [];
    const edgeM = streetFeatureWidthM(feature) / 2 +
      INTERIOR_SURFACE_MASK_EDGE_M;
    for (const point of sampled) {
      if (interiorSurfaceMaskContains(mask, point, edgeM)) {
        if (current.length >= 2) {
          runs.push(current);
        }
        current = [];
      } else {
        current.push(point);
      }
    }
    if (current.length >= 2) {
      runs.push(current);
    }
    return runs.map((points, index) => ({
      ...feature,
      id: `${feature.id}:outside-${mask.polygon.outer.length}-${index}`,
      points,
    }));
  });
}

interface NormalizedRuntimeTileViewOptions {
  readonly lod: RuntimeTileLodLevel;
  readonly showTileDiagnostics: boolean;
  readonly showCollisionDebug: boolean;
}

interface WaterRibbonSection {
  readonly x: number;
  readonly z: number;
  readonly distanceM: number;
  readonly normalX: number;
  readonly normalZ: number;
}

function normalizeOptions(
  options: Readonly<RuntimeTileViewOptions>,
  previous?: Readonly<NormalizedRuntimeTileViewOptions>,
): NormalizedRuntimeTileViewOptions {
  return {
    lod: options.lod ?? previous?.lod ?? 0,
    showTileDiagnostics:
      options.showTileDiagnostics ?? previous?.showTileDiagnostics ?? false,
    showCollisionDebug:
      options.showCollisionDebug ?? previous?.showCollisionDebug ?? false,
  };
}

function optionsEqual(
  left: Readonly<NormalizedRuntimeTileViewOptions>,
  right: Readonly<NormalizedRuntimeTileViewOptions>,
): boolean {
  return left.lod === right.lod &&
    left.showTileDiagnostics === right.showTileDiagnostics &&
    left.showCollisionDebug === right.showCollisionDebug;
}

function sourceCountsFor(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  lod: RuntimeTileLodLevel,
): RuntimeTileFeatureCounts {
  if (lod === 0) {
    return {
      buildings: tile.buildings.length,
      transportLines: tile.transportLines.length,
      waterLines: tile.waterLines.length,
      landCoverMasks: tile.landCoverMasks.length,
      collisionFootprints: tile.collision.buildingFootprints.length,
      vegetationInstances: tile.vegetation?.length ?? 0,
      structureLines: tile.structureLines?.length ?? 0,
      hardscapeAreas: tile.hardscapeAreas?.length ?? 0,
    };
  }
  return lodPayload(tile, lod).featureCounts;
}

function lodPayload(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  lod: 1 | 2,
): Readonly<RuntimeTileLodPayload> {
  const payload = tile.lods[lod - 1];
  if (payload === undefined || payload.level !== lod) {
    throw new Error(`Tile ${tile.id} is missing LOD ${lod}`);
  }
  return payload;
}

function terrainForLod(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  lod: RuntimeTileLodLevel,
): Readonly<TerrainTileData> {
  return lod === 0 ? tile.terrain : lodPayload(tile, lod).terrain;
}

function sampleTileHeight(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
): number {
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  const clamped = {
    x: Math.min(maximum, Math.max(0, x)),
    z: Math.min(maximum, Math.max(0, z)),
  };
  const height = sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    clamped,
  );
  if (height === null) {
    throw new Error(`Could not sample tile terrain at (${x}, ${z})`);
  }
  return height;
}

function sampleTileNormal(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
): readonly [number, number, number] {
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  const deltaM = Math.min(1, terrain.spacingM / 2);
  const westX = Math.max(0, x - deltaM);
  const eastX = Math.min(maximum, x + deltaM);
  const northZ = Math.max(0, z - deltaM);
  const southZ = Math.min(maximum, z + deltaM);
  const runX = Math.max(1e-6, eastX - westX);
  const runZ = Math.max(1e-6, southZ - northZ);
  const slopeX =
    (
      sampleTileHeight(terrain, eastX, z) -
      sampleTileHeight(terrain, westX, z)
    ) / runX;
  const slopeZ =
    (
      sampleTileHeight(terrain, x, southZ) -
      sampleTileHeight(terrain, x, northZ)
    ) / runZ;
  const length = Math.hypot(slopeX, 1, slopeZ);
  return [-slopeX / length, 1 / length, -slopeZ / length];
}

function createTerrainMesh(
  tileId: WorldTileId,
  tileOrigin: Readonly<{ x: number; z: number }>,
  terrain: Readonly<TerrainTileData>,
  lod: RuntimeTileLodLevel,
  materials: CityMaterialLibrary,
): Mesh {
  const geometry = createTerrainGeometry(terrain);
  const position = geometry.getAttribute("position");
  const colors: number[] = [];
  const base = new Color(
    lod === 0 ? 0x767a68 : lod === 1 ? 0x74796a : 0x777b6f,
  );
  for (let index = 0; index < position.count; index += 1) {
    const globalX = tileOrigin.x + position.getX(index);
    const globalZ = tileOrigin.z + position.getZ(index);
    const broad =
      Math.sin(globalX * 0.023) * Math.cos(globalZ * 0.019) * 0.62 +
      Math.sin((globalX + globalZ) * 0.008) * 0.38;
    const color = base.clone().offsetHSL(
      broad * 0.008,
      broad * 0.018,
      broad * 0.024,
    );
    colors.push(color.r, color.g, color.b);
  }
  geometry.setAttribute("color", new Float32BufferAttribute(colors, 3));
  const mesh = new Mesh(geometry, materials.terrain[lod]);
  mesh.name = `runtime-terrain:${tileId}:lod-${lod}`;
  mesh.receiveShadow = lod < 2;
  mesh.userData = { tileId, lod, sourceLayer: "terrain" };
  return mesh;
}

function mergeGeometryBatch(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry | null {
  if (geometries.length === 0) {
    return null;
  }
  const merged = geometries.length === 1
    ? geometries[0]
    : mergeGeometries([...geometries]);
  if (merged === null || merged === undefined) {
    for (const geometry of geometries) {
      geometry.dispose();
    }
    throw new Error(`Could not merge ${name}`);
  }
  if (geometries.length > 1) {
    for (const geometry of geometries) {
      geometry.dispose();
    }
  }
  merged.name = name;
  return merged;
}

function createLodBuildingBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  lod: 1 | 2,
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
): Group | null {
  const payload = lodPayload(tile, lod);
  if (payload.buildingBounds.length === 0) {
    return null;
  }
  const includedBuildingIds = new Set(
    payload.buildingBounds.map((building) => building.buildingId),
  );
  const buildings = tile.buildings.filter((building) =>
    includedBuildingIds.has(building.id) &&
    !isAuthoredLandmarkFootprint(building.id)
  );
  if (buildings.length === 0) {
    return null;
  }
  const group = createDistrictBuildingBatches(
    tile,
    buildings,
    materials,
    buildingConfig,
    {
      heightScale: lod === 1
        ? buildingConfig.lod1HeightScale
        : buildingConfig.lod2HeightScale,
      castShadow: false,
      includeOldTownDetails: false,
    },
  );
  group.userData = {
    ...group.userData,
    lod,
    sourceLayer: "source-building-footprints",
    buildingIds: buildings.map((building) => building.id),
    representation: "footprint-preserving-vilnius-lod-silhouette",
  };
  return group;
}

function createDistrictBuildingBatches(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  buildings: readonly Readonly<BuildingFootprintData>[],
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
  options: Readonly<{
    heightScale?: number;
    castShadow?: boolean;
    includeOldTownDetails?: boolean;
  }> = {},
): Group {
  const group = new Group();
  group.name = `runtime-buildings:${tile.id}`;
  const zverynas: Readonly<BuildingFootprintData>[] = [];
  const snipiskes: Readonly<BuildingFootprintData>[] = [];
  const antakalnisSuburban: Readonly<BuildingFootprintData>[] = [];
  const naujamiestisIndustrial: Readonly<BuildingFootprintData>[] = [];
  const sovietPanel: Readonly<BuildingFootprintData>[] = [];
  const oldTown: Readonly<BuildingFootprintData>[] = [];
  for (const building of buildings) {
    if (isZverynasTimberBackgroundBuilding(tile.id, building)) {
      zverynas.push(building);
    } else if (
      isSnipiskesWoodenBackgroundCandidate(building, tile.origin)
    ) {
      snipiskes.push(building);
    } else if (
      isAntakalnisSuburbanBackgroundCandidate(building, tile.origin)
    ) {
      antakalnisSuburban.push(building);
    } else if (
      isNaujamiestisIndustrialBackgroundCandidate(building, tile.origin)
    ) {
      naujamiestisIndustrial.push(building);
    } else if (isSovietPanelBackgroundCandidate(building, tile.origin)) {
      sovietPanel.push(building);
    } else {
      oldTown.push(building);
    }
  }
  const frontageLines = tile.transportLines.filter(
    (feature) => feature.kind === "road" || feature.kind === "path",
  );
  for (const [family, entries] of [
    ["old_town", oldTown],
    ["antakalnis_suburban", antakalnisSuburban],
    ["naujamiestis_industrial", naujamiestisIndustrial],
    ["soviet_panel", sovietPanel],
    ["zverynas", zverynas],
    ["snipiskes", snipiskes],
  ] as const) {
    if (entries.length === 0) {
      continue;
    }
    const batch = createStylizedBuildingBatch(
      entries,
      materials,
      buildingConfig,
      {
        tileId: `${tile.id}:${family}`,
        sourceTileId: tile.id,
        tileOrigin: tile.origin,
        styleFamilyOverride: family,
        frontageLines,
        ...options,
      },
    );
    group.add(batch.object);
  }
  group.userData = {
    tileId: tile.id,
    buildingIds: buildings.map((building) => building.id),
    sovietPanelBuildingIds: sovietPanel.map((building) => building.id),
    zverynasTimberBuildingIds: zverynas.map((building) => building.id),
    snipiskesTimberBuildingIds: snipiskes.map((building) => building.id),
    naujamiestisIndustrialBuildingIds: naujamiestisIndustrial.map(
      (building) => building.id,
    ),
    antakalnisSuburbanBuildingIds: antakalnisSuburban.map(
      (building) => building.id,
    ),
    representation: "deterministic-vilnius-building-visual-spec",
    districtRepresentation: "district-family-source-footprint-buildings",
  };
  return group;
}

function isZverynasTimberBackgroundBuilding(
  tileId: string,
  building: Readonly<BuildingFootprintData>,
): boolean {
  if (!ZVERYNAS_TIMBER_STYLE_TILE_IDS.has(tileId)) {
    return false;
  }
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const point of building.footprint.outer) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minZ = Math.min(minZ, point.z);
    maxZ = Math.max(maxZ, point.z);
  }
  const maximumSpanM = Math.max(maxX - minX, maxZ - minZ);
  return maximumSpanM <= ZVERYNAS_TIMBER_BACKGROUND_MAXIMUM_SPAN_M &&
    (
      building.heightM === undefined ||
      building.heightM <= ZVERYNAS_TIMBER_BACKGROUND_MAXIMUM_HEIGHT_M
    );
}

function createWaterFeatureBatch(
  tileId: WorldTileId,
  tileOrigin: Readonly<{ x: number; z: number }>,
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  bankFeatures: readonly Readonly<RuntimeLinearFeature>[],
  materials: CityMaterialLibrary,
): Mesh | null {
  const geometry = createWaterFeatureGeometry(
    features,
    terrain,
    tileOrigin,
    bankFeatures,
  );
  if (geometry === null) {
    return null;
  }
  const surface = new Mesh(geometry, materials.water);
  surface.name = `runtime-water-lines:${tileId}`;
  surface.receiveShadow = true;
  surface.userData = {
    tileId,
    sourceLayer: "water-centerlines",
    featureIds: features.map((feature) => feature.id),
    representation: "source-centerline-with-class-width-and-mapped-bank-constraints",
  };
  return surface;
}

function createWaterFeatureGeometry(
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<{ x: number; z: number }>,
  bankFeatures: readonly Readonly<RuntimeLinearFeature>[],
): BufferGeometry | null {
  const positions: number[] = [];
  const uvs: number[] = [];
  const flowDirections: number[] = [];
  const indices: number[] = [];
  for (const feature of features) {
    const sections = createWaterRibbonSections(feature.points);
    if (sections.length < 2) {
      continue;
    }
    const firstVertex = positions.length / 3;
    for (const section of sections) {
      const bankWidths = runtimeWaterBankWidthsM(
        feature,
        section,
        bankFeatures,
      );
      const surfaceY =
        runtimeWaterSurfaceElevationM(
          feature,
          terrain,
          tileOrigin,
          section,
        ) + WATER_SURFACE_LIFT_M;
      positions.push(
        section.x + section.normalX * bankWidths.leftM,
        surfaceY,
        section.z + section.normalZ * bankWidths.leftM,
        section.x - section.normalX * bankWidths.rightM,
        surfaceY,
        section.z - section.normalZ * bankWidths.rightM,
      );
      const u = section.distanceM / 8;
      uvs.push(u, 0, u, 1);
      const flowX = section.normalZ;
      const flowZ = -section.normalX;
      flowDirections.push(flowX, flowZ, flowX, flowZ);
    }
    for (let sectionIndex = 1; sectionIndex < sections.length; sectionIndex += 1) {
      const previousLeft = firstVertex + (sectionIndex - 1) * 2;
      const previousRight = previousLeft + 1;
      const nextLeft = firstVertex + sectionIndex * 2;
      const nextRight = nextLeft + 1;
      indices.push(
        previousLeft,
        nextRight,
        previousRight,
        previousLeft,
        nextLeft,
        nextRight,
      );
    }
  }
  if (positions.length === 0) {
    return null;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geometry.setAttribute(
    "waterFlowDirection",
    new Float32BufferAttribute(flowDirections, 2),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Builds one indexed ribbon for each source water line. Cross-sections share
 * vertices and use a centred tangent through bends, removing the overlapping
 * 12 m quads that previously showed as dark bands and saw-toothed banks.
 */
function createWaterRibbonSections(
  points: readonly Readonly<{ x: number; z: number }>[],
): readonly WaterRibbonSection[] {
  const sampled: Array<{ x: number; z: number; distanceM: number }> = [];
  let distanceM = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 0.01) {
      continue;
    }
    if (sampled.length === 0) {
      sampled.push({ x: from.x, z: from.z, distanceM });
    }
    const subdivisions = Math.max(1, Math.ceil(lengthM / 8));
    for (let subdivision = 1; subdivision <= subdivisions; subdivision += 1) {
      const alpha = subdivision / subdivisions;
      sampled.push({
        x: from.x + deltaX * alpha,
        z: from.z + deltaZ * alpha,
        distanceM: distanceM + lengthM * alpha,
      });
    }
    distanceM += lengthM;
  }
  return sampled.map((point, index) => {
    const previous = sampled[Math.max(0, index - 1)];
    const next = sampled[Math.min(sampled.length - 1, index + 1)];
    if (previous === undefined || next === undefined) {
      return { ...point, normalX: 0, normalZ: 1 };
    }
    const deltaX = next.x - previous.x;
    const deltaZ = next.z - previous.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 1e-6) {
      return { ...point, normalX: 0, normalZ: 1 };
    }
    return {
      ...point,
      normalX: -deltaZ / lengthM,
      normalZ: deltaX / lengthM,
    };
  });
}

function createDrapedAreaGeometry(
  polygon: Readonly<PolygonWithHoles>,
  terrain: Readonly<TerrainTileData>,
  surfaceMasks: readonly RuntimeInteriorSurfaceMask[],
): BufferGeometry {
  const sourceGeometry = new ShapeGeometry(createPolygonShape(polygon), 1);
  sourceGeometry.rotateX(-Math.PI / 2);
  const sourcePositions = sourceGeometry.getAttribute("position");
  const sourceIndices = sourceGeometry.getIndex();
  const positions: number[] = [];
  const normals: number[] = [];
  const triangleCount = sourceIndices === null
    ? Math.floor(sourcePositions.count / 3)
    : Math.floor(sourceIndices.count / 3);

  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
    const triangle = [0, 1, 2].map((corner) => {
      const sourceIndex = sourceIndices?.getX(triangleIndex * 3 + corner) ??
        triangleIndex * 3 + corner;
      return {
        x: sourcePositions.getX(sourceIndex),
        z: sourcePositions.getZ(sourceIndex),
      };
    });
    const first = triangle[0];
    const second = triangle[1];
    const third = triangle[2];
    if (first === undefined || second === undefined || third === undefined) {
      continue;
    }
    appendTerrainClippedAreaTriangle(
      positions,
      normals,
      [first, second, third],
      terrain,
      surfaceMasks,
    );
  }

  sourceGeometry.dispose();
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("normal", new Float32BufferAttribute(normals, 3));
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

interface AreaPoint {
  readonly x: number;
  readonly z: number;
}

function appendTerrainClippedAreaTriangle(
  positions: number[],
  normals: number[],
  sourceTriangle: readonly [AreaPoint, AreaPoint, AreaPoint],
  terrain: Readonly<TerrainTileData>,
  surfaceMasks: readonly RuntimeInteriorSurfaceMask[],
): void {
  const maximumCell = terrain.samplesPerSide - 2;
  const minimumX = Math.min(...sourceTriangle.map((point) => point.x));
  const maximumX = Math.max(...sourceTriangle.map((point) => point.x));
  const minimumZ = Math.min(...sourceTriangle.map((point) => point.z));
  const maximumZ = Math.max(...sourceTriangle.map((point) => point.z));
  const startColumn = Math.max(
    0,
    Math.min(maximumCell, Math.floor(minimumX / terrain.spacingM)),
  );
  const endColumn = Math.max(
    0,
    Math.min(maximumCell, Math.floor(maximumX / terrain.spacingM)),
  );
  const startRow = Math.max(
    0,
    Math.min(maximumCell, Math.floor(minimumZ / terrain.spacingM)),
  );
  const endRow = Math.max(
    0,
    Math.min(maximumCell, Math.floor(maximumZ / terrain.spacingM)),
  );

  for (let row = startRow; row <= endRow; row += 1) {
    for (let column = startColumn; column <= endColumn; column += 1) {
      const west = column * terrain.spacingM;
      const east = west + terrain.spacingM;
      const north = row * terrain.spacingM;
      const south = north + terrain.spacingM;
      const northWest = { x: west, z: north };
      const northEast = { x: east, z: north };
      const southWest = { x: west, z: south };
      const southEast = { x: east, z: south };
      for (const terrainTriangle of [
        [northWest, southWest, northEast],
        [northEast, southWest, southEast],
      ] as const) {
        const clipped = clipConvexPolygon(
          sourceTriangle,
          terrainTriangle,
        );
        const anchor = clipped[0];
        if (anchor === undefined) {
          continue;
        }
        for (let index = 2; index < clipped.length; index += 1) {
          const second = clipped[index - 1];
          const third = clipped[index];
          if (
            second === undefined ||
            third === undefined ||
            Math.abs(signedTriangleArea(anchor, second, third)) <= 1e-7
          ) {
            continue;
          }
          appendMaskedDrapedTriangle(
            positions,
            normals,
            [anchor, second, third],
            terrain,
            surfaceMasks,
          );
        }
      }
    }
  }
}

function appendMaskedDrapedTriangle(
  positions: number[],
  normals: number[],
  triangle: readonly [AreaPoint, AreaPoint, AreaPoint],
  terrain: Readonly<TerrainTileData>,
  surfaceMasks: readonly RuntimeInteriorSurfaceMask[],
): void {
  const minimumX = Math.min(...triangle.map(({ x }) => x));
  const maximumX = Math.max(...triangle.map(({ x }) => x));
  const minimumZ = Math.min(...triangle.map(({ z }) => z));
  const maximumZ = Math.max(...triangle.map(({ z }) => z));
  const relevantMasks = surfaceMasks.filter(({ bounds }) =>
    maximumX >= bounds.minX - INTERIOR_SURFACE_MASK_EDGE_M &&
    minimumX <= bounds.maxX + INTERIOR_SURFACE_MASK_EDGE_M &&
    maximumZ >= bounds.minZ - INTERIOR_SURFACE_MASK_EDGE_M &&
    minimumZ <= bounds.maxZ + INTERIOR_SURFACE_MASK_EDGE_M
  );
  if (relevantMasks.length > 0) {
    const maximumEdgeM = Math.max(
      Math.hypot(
        triangle[1].x - triangle[0].x,
        triangle[1].z - triangle[0].z,
      ),
      Math.hypot(
        triangle[2].x - triangle[1].x,
        triangle[2].z - triangle[1].z,
      ),
      Math.hypot(
        triangle[0].x - triangle[2].x,
        triangle[0].z - triangle[2].z,
      ),
    );
    if (maximumEdgeM > 0.75) {
      const midpoint = (first: AreaPoint, second: AreaPoint): AreaPoint => ({
        x: (first.x + second.x) / 2,
        z: (first.z + second.z) / 2,
      });
      const midpoint01 = midpoint(triangle[0], triangle[1]);
      const midpoint12 = midpoint(triangle[1], triangle[2]);
      const midpoint20 = midpoint(triangle[2], triangle[0]);
      for (const child of [
        [triangle[0], midpoint01, midpoint20],
        [midpoint01, triangle[1], midpoint12],
        [midpoint20, midpoint12, triangle[2]],
        [midpoint01, midpoint12, midpoint20],
      ] as const) {
        appendMaskedDrapedTriangle(
          positions,
          normals,
          child,
          terrain,
          relevantMasks,
        );
      }
      return;
    }
    const centroid = {
      x: (triangle[0].x + triangle[1].x + triangle[2].x) / 3,
      z: (triangle[0].z + triangle[1].z + triangle[2].z) / 3,
    };
    if (relevantMasks.some((mask) => interiorSurfaceMaskContains(mask, centroid))) {
      return;
    }
  }
  for (const point of triangle) {
    positions.push(
      point.x,
      sampleTileHeight(terrain, point.x, point.z) +
        LAND_COVER_SURFACE_LIFT_M,
      point.z,
    );
    normals.push(...sampleTileNormal(terrain, point.x, point.z));
  }
}

function clipConvexPolygon(
  subject: readonly AreaPoint[],
  clip: readonly AreaPoint[],
): readonly AreaPoint[] {
  const orientation = signedPolygonArea(clip) >= 0 ? 1 : -1;
  let output = [...subject];
  for (let edgeIndex = 0; edgeIndex < clip.length; edgeIndex += 1) {
    const edgeStart = clip[edgeIndex];
    const edgeEnd = clip[(edgeIndex + 1) % clip.length];
    if (edgeStart === undefined || edgeEnd === undefined) {
      continue;
    }
    const input = output;
    output = [];
    let previous = input.at(-1);
    if (previous === undefined) {
      break;
    }
    let previousDistance = signedEdgeDistance(
      edgeStart,
      edgeEnd,
      previous,
      orientation,
    );
    for (const current of input) {
      const currentDistance = signedEdgeDistance(
        edgeStart,
        edgeEnd,
        current,
        orientation,
      );
      const previousInside = previousDistance >= -1e-7;
      const currentInside = currentDistance >= -1e-7;
      if (previousInside !== currentInside) {
        const denominator = previousDistance - currentDistance;
        const t = Math.abs(denominator) <= 1e-12
          ? 0
          : previousDistance / denominator;
        output.push({
          x: previous.x + (current.x - previous.x) * t,
          z: previous.z + (current.z - previous.z) * t,
        });
      }
      if (currentInside) {
        output.push(current);
      }
      previous = current;
      previousDistance = currentDistance;
    }
  }
  return output;
}

function signedEdgeDistance(
  edgeStart: Readonly<AreaPoint>,
  edgeEnd: Readonly<AreaPoint>,
  point: Readonly<AreaPoint>,
  orientation: number,
): number {
  return orientation * (
    (edgeEnd.x - edgeStart.x) * (point.z - edgeStart.z) -
    (edgeEnd.z - edgeStart.z) * (point.x - edgeStart.x)
  );
}

function signedTriangleArea(
  first: Readonly<AreaPoint>,
  second: Readonly<AreaPoint>,
  third: Readonly<AreaPoint>,
): number {
  return (
    (second.x - first.x) * (third.z - first.z) -
    (second.z - first.z) * (third.x - first.x)
  ) / 2;
}

function signedPolygonArea(points: readonly AreaPoint[]): number {
  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    const next = points[(index + 1) % points.length];
    if (point !== undefined && next !== undefined) {
      twiceArea += point.x * next.z - next.x * point.z;
    }
  }
  return twiceArea / 2;
}

function createLandCoverMesh(
  tileId: WorldTileId,
  features: readonly Readonly<RuntimeAreaFeature>[],
  terrain: Readonly<TerrainTileData>,
  materials: CityMaterialLibrary,
  surfaceMasks: readonly RuntimeInteriorSurfaceMask[],
): Mesh | null {
  // Clip every source face to the rendered heightfield triangles. Sampling
  // boundary-only polygons, or merely subdividing them, leaves slightly
  // different planes that show up as giant lit wedges and depth seams.
  // Matching the terrain topology plus deterministic terrain normals keeps
  // adjacent source pieces visually continuous.
  const geometries = features.flatMap((feature) =>
    feature.polygons
      .map((polygon) =>
        createDrapedAreaGeometry(polygon, terrain, surfaceMasks)
      )
  );
  const geometry = mergeGeometryBatch(
    geometries,
    `runtime-land-cover:${tileId}`,
  );
  if (geometry === null) {
    return null;
  }
  const mesh = new Mesh(geometry, materials.landCover);
  mesh.name = `runtime-land-cover:${tileId}`;
  mesh.receiveShadow = true;
  mesh.userData = {
    tileId,
    sourceLayer: "land-cover",
    featureIds: features.map((feature) => feature.id),
  };
  return mesh;
}

function appendRingSegments(
  positions: number[],
  ring: Readonly<PolygonWithHoles["outer"]>,
  elevationM: number,
): void {
  for (let index = 1; index < ring.length; index += 1) {
    const from = ring[index - 1];
    const to = ring[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    positions.push(
      from.x,
      elevationM,
      from.z,
      to.x,
      elevationM,
      to.z,
    );
  }
}

function createCollisionDebugLines(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
): LineSegments | null {
  const positions: number[] = [];
  for (const collision of tile.collision.buildingFootprints) {
    const elevation = collision.baseElevationM + COLLISION_LINE_LIFT_M;
    appendRingSegments(positions, collision.footprint.outer, elevation);
    for (const hole of collision.footprint.holes) {
      appendRingSegments(positions, hole, elevation);
    }
  }
  if (positions.length === 0) {
    return null;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  const lines = new LineSegments(geometry, COLLISION_MATERIAL);
  lines.name = `runtime-collision-debug:${tile.id}`;
  lines.renderOrder = 20;
  lines.userData = {
    tileId: tile.id,
    sourceLayer: "collision-building-footprints",
    representation: "flat-outline-no-height",
  };
  return lines;
}

function createTileDiagnosticLines(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  lod: RuntimeTileLodLevel,
): LineSegments {
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  const corners = [
    { x: 0, z: 0 },
    { x: maximum, z: 0 },
    { x: maximum, z: maximum },
    { x: 0, z: maximum },
  ] as const;
  const positions: number[] = [];
  for (let index = 0; index < corners.length; index += 1) {
    const from = corners[index];
    const to = corners[(index + 1) % corners.length];
    if (from === undefined || to === undefined) {
      continue;
    }
    positions.push(
      from.x,
      sampleTileHeight(terrain, from.x, from.z) + TILE_DIAGNOSTIC_LIFT_M,
      from.z,
      to.x,
      sampleTileHeight(terrain, to.x, to.z) + TILE_DIAGNOSTIC_LIFT_M,
      to.z,
    );
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  const lines = new LineSegments(geometry, TILE_DIAGNOSTIC_MATERIAL);
  lines.name = `runtime-tile-diagnostic:${tile.id}:lod-${lod}`;
  lines.renderOrder = 21;
  return lines;
}

function disposeGroupGeometries(group: Group): number {
  const geometries = new Set<BufferGeometry>();
  group.traverse((object) => {
    if (object instanceof Mesh || object instanceof LineSegments) {
      geometries.add(object.geometry);
    }
  });
  for (const geometry of geometries) {
    geometry.dispose();
  }
  group.clear();
  return geometries.size;
}

function countTriangles(group: Group): number {
  let triangleCount = 0;
  group.traverse((object) => {
    if (!(object instanceof Mesh)) {
      return;
    }
    const geometry = object.geometry;
    triangleCount += geometry.index === null
      ? Math.floor(geometry.getAttribute("position").count / 3)
      : Math.floor(geometry.index.count / 3);
  });
  return triangleCount;
}

function countRenderResources(
  group: Object3D,
): Readonly<{ geometryCount: number; drawCallEstimate: number }> {
  const geometries = new Set<BufferGeometry>();
  let drawCallEstimate = 0;
  group.traverse((object) => {
    if (object instanceof Mesh || object instanceof LineSegments) {
      geometries.add(object.geometry);
      drawCallEstimate += 1;
    }
  });
  return { geometryCount: geometries.size, drawCallEstimate };
}

interface PreparedRuntimeBuildingPhase {
  readonly object: Group | null;
  readonly buildingPlans: number;
  readonly buildCpuMs: number;
}

function prepareDetailedRuntimeBuildings(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
): PreparedRuntimeBuildingPhase {
  const proceduralBuildings = tile.buildings.filter(
    (building) => !isAuthoredLandmarkFootprint(building.id),
  );
  if (proceduralBuildings.length === 0) {
    return { object: null, buildingPlans: 0, buildCpuMs: 0 };
  }
  const startedAt = performance.now();
  const object = createDistrictBuildingBatches(
    tile,
    proceduralBuildings,
    materials,
    buildingConfig,
  );
  return {
    object,
    buildingPlans: proceduralBuildings.length,
    buildCpuMs: performance.now() - startedAt,
  };
}

function buildTileGroup(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  options: Readonly<NormalizedRuntimeTileViewOptions>,
  resourceVersion: number,
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
  preparedBuildingPhase?: Readonly<PreparedRuntimeBuildingPhase>,
): Readonly<{ group: Group; metrics: RuntimeTileRenderMetrics }> {
  const buildCpuMsByGroup: Record<string, number> = {};
  const measure = <Value>(label: string, operation: () => Value): Value => {
    const startedAt = performance.now();
    const value = operation();
    buildCpuMsByGroup[label] =
      (buildCpuMsByGroup[label] ?? 0) + performance.now() - startedAt;
    return value;
  };
  const group = new Group();
  group.name = `runtime-tile:${tile.id}`;
  group.position.set(tile.origin.x, 0, tile.origin.z);
  group.userData = {
    tileId: tile.id,
    lod: options.lod,
    sourceIds: [...tile.sourceIds],
  };
  const terrain = terrainWithAuthoredStreetEarthworks(
    terrainForLod(tile, options.lod),
    tile.origin,
  );
  const renderTerrain = terrainWithRuntimeWaterCarving(
    tile.origin,
    terrain,
    tile.waterLines,
    tile.transportLines,
  );
  const interiorSurfaceMasks = interiorSurfaceMasksForTile(
    tile.id,
    tile.origin,
  );
  const renderTransportFeatures = transportFeaturesOutsideInteriorSurfaceMasks(
    tile.transportLines,
    interiorSurfaceMasks,
  );
  group.add(
    measure("terrain", () => createTerrainMesh(
      tile.id,
      tile.origin,
      renderTerrain,
      options.lod,
      materials,
    )),
  );

  let buildingPlans = 0;
  let transportLines = 0;
  let waterLines = 0;
  let landCoverPolygons = 0;
  let trees = 0;
  let busStops = 0;
  let trafficSignals = 0;
  let roadMarkingSegments = 0;
  let roadSigns = 0;
  let collisionFootprints = 0;
  const vegetation = measure(
    "vegetation-data",
    () => vegetationInstancesForTile(tile, renderTerrain),
  );

  if (options.lod === 0) {
    const landCover = measure("land-cover", () => createLandCoverMesh(
      tile.id,
      tile.landCoverMasks,
      renderTerrain,
      materials,
      interiorSurfaceMasks,
    ));
    if (landCover !== null) {
      group.add(landCover);
      landCoverPolygons = tile.landCoverMasks.reduce(
        (count, feature) => count + feature.polygons.length,
        0,
      );
    }
    const urbanStructures = measure("urban-structures", () =>
      createUrbanStructureBatch(
      tile.id,
      tile.structureLines ?? [],
      tile.hardscapeAreas ?? [],
      renderTerrain,
      streetSurfaceFamilyForTile(tile.origin, tile.sizeM),
      materials,
      ));
    if (urbanStructures !== null) {
      group.add(urbanStructures.object);
    }
    const streets = measure("street-surfaces", () => createStreetSurfaceBatch(
      tile.id,
      renderTransportFeatures,
      renderTerrain,
      streetSurfaceFamilyForTile(tile.origin, tile.sizeM),
      materials,
    ));
    if (streets !== null) {
      group.add(streets.object);
      transportLines =
        streets.carriagewayCount + streets.pedestrianCount;
    }
    const roadMarkingBatch = measure(
      "road-markings",
      () => createRoadMarkingBatch(
        tile,
        renderTerrain,
        streetSurfaceFamilyForTile(tile.origin, tile.sizeM),
        materials,
      ),
    );
    if (roadMarkingBatch !== null) {
      group.add(roadMarkingBatch.object);
      roadMarkingSegments = roadMarkingBatch.ribbonSegmentCount;
    }
    const crossingDetails = measure(
      "crossing-details",
      () => createCrossingDetailBatch(
        tile,
        renderTerrain,
        materials,
      ),
    );
    if (crossingDetails !== null) {
      group.add(crossingDetails.object);
    }
    const streetFurniture = measure(
      "street-furniture",
      () => createStreetFurnitureBatch(tile, renderTerrain, materials),
    );
    if (streetFurniture !== null) {
      group.add(streetFurniture.object);
    }
    const streetNamePlaques = measure(
      "street-name-plaques",
      () => createStreetNamePlaqueBatch(tile, renderTerrain, materials),
    );
    if (streetNamePlaques !== null) {
      group.add(streetNamePlaques.object);
    }
    const railways = measure("railways", () => createRailwaySurfaceBatch(
      tile.id,
      tile.transportLines,
      renderTerrain,
      materials,
      true,
      tile.railwayEquipment ?? [],
    ));
    if (railways !== null) {
      group.add(railways.object);
      transportLines += railways.trackCount;
    }
    const water = measure("water", () => createWaterFeatureBatch(
      tile.id,
      tile.origin,
      tile.waterLines,
      terrain,
      tile.transportLines,
      materials,
    ));
    if (water !== null) {
      group.add(water);
      waterLines = tile.waterLines.length;
    }
    const streetLights = measure(
      "street-lights",
      () => createStreetLightBatch(tile, materials, renderTerrain),
    );
    if (streetLights !== null) {
      group.add(streetLights.object);
    }
    const trafficSignalBatch = measure(
      "traffic-signals",
      () => createTrafficSignalBatch(tile, materials, renderTerrain),
    );
    if (trafficSignalBatch !== null) {
      group.add(trafficSignalBatch.object);
      trafficSignals = trafficSignalBatch.signalCount;
    }
    const roadSignBatch = measure(
      "road-signs",
      () => createRoadSignBatch(tile, renderTerrain, materials),
    );
    if (roadSignBatch !== null) {
      group.add(roadSignBatch.object);
      roadSigns = roadSignBatch.signCount;
    }
    const treeBatch = measure("trees", () => createRuntimeTreeBatch(
      tile.id,
      vegetation,
      options.lod,
      materials,
    ));
    if (treeBatch !== null) {
      group.add(treeBatch.object);
      trees = treeBatch.treeCount;
    }
    const buildingPhase = preparedBuildingPhase ??
      prepareDetailedRuntimeBuildings(tile, materials, buildingConfig);
    buildCpuMsByGroup.buildings = buildingPhase.buildCpuMs;
    if (buildingPhase.object !== null) {
      group.add(buildingPhase.object);
      buildingPlans = buildingPhase.buildingPlans;
    }
    if (options.showCollisionDebug) {
      const collision = createCollisionDebugLines(tile);
      if (collision !== null) {
        group.add(collision);
        collisionFootprints = tile.collision.buildingFootprints.length;
      }
    }
  } else {
    const landCover = measure("land-cover", () => createLandCoverMesh(
      tile.id,
      tile.landCoverMasks,
      renderTerrain,
      materials,
      interiorSurfaceMasks,
    ));
    if (landCover !== null) {
      group.add(landCover);
      landCoverPolygons = tile.landCoverMasks.reduce(
        (count, feature) => count + feature.polygons.length,
        0,
      );
    }
    const transport = measure("street-surfaces", () =>
      createReducedStreetSurfaceBatch(
      tile.id,
      renderTransportFeatures,
      renderTerrain,
      streetSurfaceFamilyForTile(tile.origin, tile.sizeM),
      materials,
      Math.max(8, terrain.spacingM / 2),
      ));
    if (transport !== null) {
      group.add(transport.object);
      transportLines =
        transport.carriagewayCount + transport.pedestrianCount;
    }
    const railways = measure("railways", () => createRailwaySurfaceBatch(
      tile.id,
      tile.transportLines,
      renderTerrain,
      materials,
      false,
      [],
    ));
    if (railways !== null) {
      group.add(railways.object);
      transportLines += railways.trackCount;
    }
    const water = measure("water", () => createWaterFeatureBatch(
      tile.id,
      tile.origin,
      tile.waterLines,
      terrain,
      tile.transportLines,
      materials,
    ));
    if (water !== null) {
      group.add(water);
      waterLines = tile.waterLines.length;
    }
    const bounds = measure("buildings", () => createLodBuildingBatch(
      tile,
      options.lod as 1 | 2,
      materials,
      buildingConfig,
    ));
    if (bounds !== null) {
      group.add(bounds);
      buildingPlans = lodPayload(tile, options.lod).buildingBounds.filter(
        (building) => !isAuthoredLandmarkFootprint(building.buildingId),
      ).length;
    }
    const treeBatch = measure("trees", () => createRuntimeTreeBatch(
      tile.id,
      vegetation,
      options.lod,
      materials,
    ));
    if (treeBatch !== null) {
      group.add(treeBatch.object);
      trees = treeBatch.treeCount;
    }
  }

  const busStopBatch = measure(
    "bus-stops",
    () => createBusStopBatch(
      tile,
      materials,
      renderTerrain,
      { lod: options.lod },
    ),
  );
  if (busStopBatch !== null) {
    group.add(busStopBatch.object);
    busStops = busStopBatch.stopCount;
  }

  if (options.showTileDiagnostics) {
    group.add(createTileDiagnosticLines(tile, terrain, options.lod));
  }

  const resources = measure("resource-diagnostics", () =>
    countRenderResources(group)
  );
  const drawCallsByGroup = Object.freeze(Object.fromEntries(
    group.children.map((child) => [
      child.name.replace(`:${tile.id}`, ""),
      countRenderResources(child).drawCallEstimate,
    ]),
  ));
  const trianglesByGroup = Object.freeze(Object.fromEntries(
    group.children.map((child) => [
      child.name.replace(`:${tile.id}`, ""),
      countTriangles(child as Group),
    ]),
  ));
  return {
    group,
    metrics: {
      tileId: tile.id,
      lod: options.lod,
      geometryCount: resources.geometryCount,
      drawCallEstimate: resources.drawCallEstimate,
      triangleCount: countTriangles(group),
      drawCallsByGroup,
      trianglesByGroup,
      buildCpuMsByGroup,
      rendered: {
        buildingPlans,
        transportLines,
        waterLines,
        landCoverPolygons,
        trees,
        busStops,
        trafficSignals,
        roadMarkingSegments,
        roadSigns,
        collisionFootprints,
      },
      source: sourceCountsFor(tile, options.lod),
      collisionDebugVisible:
        options.lod === 0 && options.showCollisionDebug,
      resourceVersion,
    },
  };
}

export function createRuntimeWorldTileView(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
  initialOptions: Readonly<RuntimeTileViewOptions> = {},
  preparedBuildingPhase?: Readonly<PreparedRuntimeBuildingPhase>,
): RuntimeWorldTileView {
  const object = new Group();
  object.name = `runtime-tile-container:${tile.id}`;
  let options = normalizeOptions(initialOptions);
  let resourceVersion = 1;
  let build = buildTileGroup(
    tile,
    options,
    resourceVersion,
    materials,
    buildingConfig,
    preparedBuildingPhase,
  );
  object.add(build.group);
  let disposed = false;

  return {
    object,
    get metrics(): RuntimeTileRenderMetrics {
      return build.metrics;
    },
    getCameraObstructions(): readonly RuntimeTileCameraObstruction[] {
      const obstructionObject = options.lod === 0
        ? build.group.getObjectByName(`runtime-buildings:${tile.id}`)
        : build.group.getObjectByName(`runtime-buildings:${tile.id}`);
      if (
        !(obstructionObject instanceof Mesh) &&
        !(obstructionObject instanceof Group)
      ) {
        return [];
      }
      const buildingIds = options.lod === 0
        ? tile.buildings
          .filter((building) => !isAuthoredLandmarkFootprint(building.id))
          .map((building) => building.id)
        : lodPayload(tile, options.lod).buildingBounds.map(
            (building) => building.buildingId,
          ).filter((buildingId) => !isAuthoredLandmarkFootprint(buildingId));
      return buildingIds.map((buildingId) => ({
        id: `building:${tile.id}:${buildingId}`,
        object: obstructionObject,
      }));
    },
    update(nextOptions): RuntimeTileRenderMetrics {
      if (disposed) {
        throw new Error(`Runtime tile view ${tile.id} is disposed`);
      }
      const normalized = normalizeOptions(nextOptions, options);
      if (optionsEqual(options, normalized)) {
        return build.metrics;
      }
      disposeGroupGeometries(build.group);
      build.group.removeFromParent();
      options = normalized;
      resourceVersion += 1;
      build = buildTileGroup(
        tile,
        options,
        resourceVersion,
        materials,
        buildingConfig,
      );
      object.add(build.group);
      return build.metrics;
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      disposeGroupGeometries(build.group);
      build.group.removeFromParent();
      object.removeFromParent();
      object.clear();
    },
  };
}

async function createRuntimeWorldTileViewInPhases(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
  initialOptions: Readonly<RuntimeTileViewOptions>,
  yieldToNextFrame: () => Promise<void>,
  shouldContinue: () => boolean,
): Promise<RuntimeWorldTileView | null> {
  const normalized = normalizeOptions(initialOptions);
  if (normalized.lod !== 0) {
    return shouldContinue()
      ? createRuntimeWorldTileView(
          tile,
          materials,
          buildingConfig,
          initialOptions,
        )
      : null;
  }
  const preparedBuildingPhase = prepareDetailedRuntimeBuildings(
    tile,
    materials,
    buildingConfig,
  );
  await yieldToNextFrame();
  if (!shouldContinue()) {
    if (preparedBuildingPhase.object !== null) {
      disposeGroupGeometries(preparedBuildingPhase.object);
    }
    return null;
  }
  return createRuntimeWorldTileView(
    tile,
    materials,
    buildingConfig,
    initialOptions,
    preparedBuildingPhase,
  );
}

export function createStreamingWorldView(
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
): StreamingWorldView {
  const object = new Group();
  object.name = "streaming-runtime-world";
  const tiles = new Map<WorldTileId, RuntimeWorldTileView>();
  let attachOperations = 0;
  let updateOperations = 0;
  let detachOperations = 0;
  let disposedGeometryCount = 0;
  let disposed = false;

  function ensureActive(): void {
    if (disposed) {
      throw new Error("Streaming world view is disposed");
    }
  }

  function detach(tileId: WorldTileId): boolean {
    ensureActive();
    const tile = tiles.get(tileId);
    if (tile === undefined) {
      return false;
    }
    disposedGeometryCount += tile.metrics.geometryCount;
    tile.dispose();
    tiles.delete(tileId);
    detachOperations += 1;
    return true;
  }

  function getDiagnostics(): StreamingWorldRenderDiagnostics {
    const tilesByLod: Record<RuntimeTileLodLevel, number> = {
      0: 0,
      1: 0,
      2: 0,
    };
    let geometryCount = 0;
    let drawCallEstimate = 0;
    let triangleCount = 0;
    const drawCallsByGroup: Record<string, number> = {};
    const trianglesByGroup: Record<string, number> = {};
    const buildCpuMsByGroup: Record<string, number> = {};
    const buildCpuMsByTile: Record<string, Readonly<Record<string, number>>> = {};
    let collisionDebugTileCount = 0;
    for (const tile of tiles.values()) {
      const metrics = tile.metrics;
      buildCpuMsByTile[metrics.tileId] = metrics.buildCpuMsByGroup;
      tilesByLod[metrics.lod] += 1;
      geometryCount += metrics.geometryCount;
      drawCallEstimate += metrics.drawCallEstimate;
      triangleCount += metrics.triangleCount;
      for (const [groupName, drawCalls] of Object.entries(
        metrics.drawCallsByGroup,
      )) {
        drawCallsByGroup[groupName] =
          (drawCallsByGroup[groupName] ?? 0) + drawCalls;
      }
      for (const [groupName, triangles] of Object.entries(
        metrics.trianglesByGroup,
      )) {
        trianglesByGroup[groupName] =
          (trianglesByGroup[groupName] ?? 0) + triangles;
      }
      for (const [groupName, elapsedMs] of Object.entries(
        metrics.buildCpuMsByGroup,
      )) {
        buildCpuMsByGroup[groupName] =
          (buildCpuMsByGroup[groupName] ?? 0) + elapsedMs;
      }
      collisionDebugTileCount += Number(metrics.collisionDebugVisible);
    }
    return {
      attachedTileCount: tiles.size,
      tilesByLod,
      geometryCount,
      drawCallEstimate,
      triangleCount,
      drawCallsByGroup,
      trianglesByGroup,
      buildCpuMsByGroup,
      buildCpuMsByTile,
      collisionDebugTileCount,
      attachOperations,
      updateOperations,
      detachOperations,
      disposedGeometryCount,
    };
  }

  return {
    object,
    attach(tile, options = {}): RuntimeTileRenderMetrics {
      ensureActive();
      detach(tile.id);
      const view = createRuntimeWorldTileView(
        tile,
        materials,
        buildingConfig,
        options,
      );
      tiles.set(tile.id, view);
      object.add(view.object);
      attachOperations += 1;
      return view.metrics;
    },
    async attachInPhases(
      tile,
      options,
      yieldToNextFrame,
      shouldContinue,
    ): Promise<RuntimeTileRenderMetrics | null> {
      ensureActive();
      const view = await createRuntimeWorldTileViewInPhases(
        tile,
        materials,
        buildingConfig,
        options,
        yieldToNextFrame,
        shouldContinue,
      );
      if (view === null || !shouldContinue()) {
        view?.dispose();
        return null;
      }
      detach(tile.id);
      tiles.set(tile.id, view);
      object.add(view.object);
      attachOperations += 1;
      return view.metrics;
    },
    update(tileId, options): RuntimeTileRenderMetrics {
      ensureActive();
      const tile = tiles.get(tileId);
      if (tile === undefined) {
        throw new Error(`Runtime tile ${tileId} is not attached`);
      }
      const previousGeometryCount = tile.metrics.geometryCount;
      const previousVersion = tile.metrics.resourceVersion;
      const metrics = tile.update(options);
      if (metrics.resourceVersion !== previousVersion) {
        disposedGeometryCount += previousGeometryCount;
      }
      updateOperations += 1;
      return metrics;
    },
    detach,
    has(tileId): boolean {
      return tiles.has(tileId);
    },
    getCameraObstructions(
      tileId,
    ): readonly RuntimeTileCameraObstruction[] {
      return tiles.get(tileId)?.getCameraObstructions() ?? [];
    },
    getDiagnostics,
    clear(): void {
      ensureActive();
      for (const tileId of [...tiles.keys()]) {
        detach(tileId);
      }
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      for (const tile of tiles.values()) {
        disposedGeometryCount += tile.metrics.geometryCount;
        tile.dispose();
      }
      tiles.clear();
      object.removeFromParent();
      object.clear();
      disposed = true;
    },
  };
}
